/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PLACEHOLDER_IMAGES } from '@sportninja/common/constants/placeholderImages'
import { weightBold } from 'src/components/css'

interface TeamNameCellProps {
  name: string
  imageUrl: string | undefined
  /**
   * Seed, shown between the drag grip and the crest. It has no column of its
   * own — the number is 1-2 digits and the record bar uses the width better.
   * Omit it where there is no seed to show; `null` renders an em dash so the
   * crests still line up down the column.
   */
  seed?: number | null
}

/**
 * Small round crest + name, matching the mockup's team-row treatment.
 *
 * A team without a logo shows the shared team placeholder rather than
 * initials, matching `components/panel/PanelHeader.tsx` so the same team looks
 * the same in the panel header and in this row.
 */
const TeamNameCell = ({ name, imageUrl, seed }: TeamNameCellProps) => {
  'use memo'

  // Keyed by URL, not a bare boolean: AG Grid reuses this cell across rows and
  // across a logo change, so a flag that never resets would strand a team on
  // the placeholder after its logo starts working.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const src =
    imageUrl && failedUrl !== imageUrl ? imageUrl : PLACEHOLDER_IMAGES.team

  return (
    <span css={rowStyle}>
      {seed !== undefined && (
        <span css={seedStyle}>{seed === null ? '—' : seed}</span>
      )}
      <img
        css={crestStyle}
        src={src}
        alt=''
        // Catches a URL that resolves but 404s, which would otherwise leave an
        // empty box where the crest should be.
        onError={() => imageUrl && setFailedUrl(imageUrl)}
      />
      <span css={nameStyle}>{name}</span>
    </span>
  )
}

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
`

/* Sits between the grip and the crest, as tight as it goes: a fixed width so
   every crest still starts at the same x whether the seed is 1 or 12, tabular
   figures so the digits do not jitter, and its own small right margin instead
   of the row's wider gap. Muted -- it labels the row, it is not its subject. */
const seedStyle = css`
  flex: none;
  min-width: 12px;
  margin-right: -3px;
  text-align: right;
  color: ${colors.NEUTRAL_100};
  font-variant-numeric: tabular-nums;
`

const crestStyle = css`
  ${weightBold}
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  display: flex;
  align-items: center;
  justify-content: center;
  /* contain, not cover — crests are often non-square and must not be cropped */
  object-fit: contain;
  padding: 2px;
  font-size: 10px;
`

const nameStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default TeamNameCell
